import { useCallback, useRef, useState } from 'react';
import { describeUnknownAgreementFailure } from '@/modules/agreements/shared';
import { useNotification } from '@/shared/ui/notification';
import type { NotificationOptions } from '@/shared/ui/notification';
import {
  emptyDraft,
  emptyProgress,
} from '@/modules/agreements/platform/pages/document-create-page/types';
import type {
  DocumentDraft,
  DocumentProgress,
} from '@/modules/agreements/platform/pages/document-create-page/types';
import { firstIncompleteStep } from '@/modules/agreements/platform/pages/document-create-page/lib/validation';
import {
  writeDocument,
  writeRequirement,
  writeVersion,
} from '@/modules/agreements/platform/pages/document-create-page/lib/writers';

/**
 * The authoring flow's state and its three sequential writes.
 *
 * WHY THREE CALLS AND NOT ONE
 * ---------------------------
 * The API has no "create a complete agreement" endpoint. A document is created,
 * a version is published against it, and a requirement binds a subject to that
 * version — three routes, three possible failures. The stepper does not hide
 * that; it sequences it, so the operator sees one journey while the console
 * still makes the calls the server actually offers.
 *
 * PARTIAL FAILURE IS A REAL STATE, NOT AN EDGE CASE
 * -------------------------------------------------
 * The calls are not atomic and there is no rollback: the API has no delete for a
 * document. If step 1 succeeds and step 2 fails, a document EXISTS. Pretending
 * otherwise would be a lie the operator discovers later, when they find a
 * half-authored document in the list. So each success is recorded in
 * `progress`, and a failure after any success is reported as a partial outcome
 * with the id of what was created — never as a plain "could not save".
 *
 * RETRY IS IDEMPOTENT BY CONSTRUCTION
 * -----------------------------------
 * A retry re-runs only the steps that have not yet succeeded, because each
 * writer in `lib/writers` returns the recorded id instead of writing again.
 *
 * WHY PROGRESS IS ALSO HELD IN A REF
 * ----------------------------------
 * `setProgress` is asynchronous, so the `progress` captured by this render is
 * still the PRE-write value when a later write throws. Reading it in the catch
 * would report "could not create document" for a document that was in fact
 * created — the exact lie this flow exists to avoid. The ref is updated
 * synchronously alongside the state, so the failure reporter always sees what
 * actually happened.
 */
export function useDocumentAuthoring(onFinished: (documentId: string) => void) {
  const { notify } = useNotification();
  const [draft, setDraft] = useState<DocumentDraft>(emptyDraft);
  const [progress, setProgress] = useState<DocumentProgress>(emptyProgress);
  const [busy, setBusy] = useState(false);
  const progressRef = useRef<DocumentProgress>(emptyProgress());

  /** Records a write's result in both the ref (synchronous) and the state (rendered). */
  const record = useCallback((patch: Partial<DocumentProgress>): void => {
    progressRef.current = { ...progressRef.current, ...patch };
    setProgress(progressRef.current);
  }, []);

  /** Merges a partial update into the draft. One setter, so no field can be missed. */
  const update = useCallback((patch: Partial<DocumentDraft>): void => {
    setDraft((previous) => ({ ...previous, ...patch }));
  }, []);

  /**
   * Runs the three writes in order, stopping at the first failure.
   *
   * Re-validates every step first: the operator can edit step 1 after reaching
   * step 3, and a submission must not ride on a pass that has since gone stale.
   */
  const submit = useCallback(async (): Promise<void> => {
    if (firstIncompleteStep(draft) >= 0) return;

    setBusy(true);
    try {
      const documentId = await writeDocument(draft, progressRef.current);
      record({ documentId });

      const versionId = await writeVersion(draft, progressRef.current, documentId);
      record({ versionId });

      const requirementId = await writeRequirement(
        draft,
        progressRef.current,
        documentId,
        versionId,
      );
      record({ requirementId });

      notify({ tone: 'success', title: 'Agreement document published' });
      onFinished(documentId);
    } catch (caught) {
      reportFailure(caught, progressRef.current, notify);
    } finally {
      setBusy(false);
    }
  }, [draft, record, notify, onFinished]);

  return { draft, update, progress, busy, submit };
}

/**
 * Reports a failed write, distinguishing a clean failure from a partial one.
 *
 * A failure BEFORE anything was created is a plain failure: nothing exists, the
 * operator retries, and no explanation is owed. A failure AFTER something was
 * created is a different message, because the operator now owns a half-authored
 * document and has to be told so — and told where it is.
 */
function reportFailure(
  caught: unknown,
  progress: DocumentProgress,
  notify: (options: NotificationOptions) => void,
): void {
  const message = describeUnknownAgreementFailure(caught);

  if (!progress.documentId) {
    notify({ tone: 'error', title: 'Could not create document', description: message });
    return;
  }

  const stage = progress.versionId ? 'requirement' : 'version';
  notify({
    tone: 'error',
    title: `Document created, but the ${stage} was not`,
    description: `${message} The document exists and is not lost — open it to finish authoring it there.`,
  });
}
